import React, { useEffect, useMemo, useState } from 'react';
import { FiPaperclip, FiTrash2, FiX } from 'shared/ui/fiIcons';
import { useI18n } from '../../../context/I18nContext';
import {
  type CalendarChecklist,
  type CalendarPriority,
  type CalendarRepeatType,
  type CalendarTask,
  type CalendarTaskLabel,
  createCalendarLabelOnline,
  deleteCalendarTaskOccurrenceOnline,
  deleteCalendarTaskOnline,
  findNearestFreeStart,
  getCalendarTaskSeriesId,
  getAttachmentDisplayName,
  saveCalendarTaskOccurrenceOnline,
  saveCalendarTaskOnline,
} from '../calendarTaskStorage';

type CalendarTaskModalProps = {
  calendarId: string;
  isOpen: boolean;
  task: CalendarTask | null;
  seriesTaskId?: string | null;
  draft?: Partial<CalendarTask> | null;
  occurrenceStartAt?: string | null;
  occurrenceDate?: string | null;
  tasks: CalendarTask[];
  labels: CalendarTaskLabel[];
  onClose: () => void;
  onSaved: (task: CalendarTask, usedAutoStart: boolean) => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
  onLabelsChange: (labels: CalendarTaskLabel[]) => void;
};

const colorOptions = ['#2563eb', '#0f766e', '#dc2626', '#ca8a04', '#7c3aed', '#db2777', '#475569'];
const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const toLocalInputValue = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const fromLocalInputValue = (value: string) => value ? new Date(value).toISOString() : '';

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getInitialTaskValues = (task: CalendarTask | null, draft?: Partial<CalendarTask> | null) => task || draft || {};

const toDateKey = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTime = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

export const CalendarTaskModal: React.FC<CalendarTaskModalProps> = ({
  calendarId,
  isOpen,
  task,
  seriesTaskId,
  draft,
  occurrenceStartAt,
  occurrenceDate,
  tasks,
  labels,
  onClose,
  onSaved,
  onDeleted,
  onLabelsChange,
}) => {
  const { t } = useI18n();
  const initial = getInitialTaskValues(task, draft);
  const [title, setTitle] = useState(initial.title || '');
  const [description, setDescription] = useState(initial.description || '');
  const [durationHours, setDurationHours] = useState(String(Math.floor((initial.durationMinutes || 60) / 60)));
  const [durationMinutes, setDurationMinutes] = useState(String((initial.durationMinutes || 60) % 60));
  const [deadlineAt, setDeadlineAt] = useState(toLocalInputValue(initial.deadlineAt));
  const [startAt, setStartAt] = useState(toLocalInputValue(initial.startAt));
  const [repeatType, setRepeatType] = useState<CalendarRepeatType>(initial.repeatType || 'none');
  const [customRepeatDays, setCustomRepeatDays] = useState<string[]>(initial.customRepeatDays || []);
  const [priority, setPriority] = useState<CalendarPriority | ''>(initial.priority || '');
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>(initial.labelIds || []);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#2563eb');
  const [headerColor, setHeaderColor] = useState(initial.headerColor || '#2563eb');
  const [checklists, setChecklists] = useState<CalendarChecklist[]>(initial.checklists || []);
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [attachments, setAttachments] = useState(initial.attachments || []);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const nextInitial = getInitialTaskValues(task, draft);
    setTitle(nextInitial.title || '');
    setDescription(nextInitial.description || '');
    setDurationHours(String(Math.floor((nextInitial.durationMinutes || 60) / 60)));
    setDurationMinutes(String((nextInitial.durationMinutes || 60) % 60));
    setDeadlineAt(toLocalInputValue(nextInitial.deadlineAt));
    setStartAt(toLocalInputValue(occurrenceStartAt || nextInitial.startAt));
    setRepeatType(nextInitial.repeatType || 'none');
    setCustomRepeatDays(nextInitial.customRepeatDays || []);
    setPriority(nextInitial.priority || '');
    setSelectedLabelIds(nextInitial.labelIds || []);
    setNewLabelName('');
    setNewLabelColor('#2563eb');
    setHeaderColor(nextInitial.headerColor || '#2563eb');
    setChecklists(nextInitial.checklists || []);
    setNewChecklistTitle('');
    setAttachments(nextInitial.attachments || []);
    setError('');
    setIsSaving(false);
  }, [draft, isOpen, occurrenceDate, occurrenceStartAt, task]);

  const durationTotal = useMemo(() => {
    const hours = Math.max(0, Number(durationHours) || 0);
    const minutes = Math.max(0, Number(durationMinutes) || 0);
    return hours * 60 + minutes;
  }, [durationHours, durationMinutes]);

  if (!isOpen) return null;

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds(prev => prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]);
  };

  const handleCreateLabel = async () => {
    const name = newLabelName.trim();
    if (!name) return;
    const label = await createCalendarLabelOnline(calendarId, name, newLabelColor);
    onLabelsChange([label, ...labels]);
    setSelectedLabelIds(prev => [...prev, label.id]);
    setNewLabelName('');
  };

  const addChecklist = () => {
    const titleValue = newChecklistTitle.trim() || t('checklist.title');
    setChecklists(prev => [...prev, { id: makeId(), title: titleValue, items: [] }]);
    setNewChecklistTitle('');
  };

  const addChecklistItem = (checklistId: string, text: string) => {
    const value = text.trim();
    if (!value) return;
    setChecklists(prev => prev.map(checklist => checklist.id === checklistId
      ? { ...checklist, items: [...checklist.items, { id: makeId(), text: value, isChecked: false }] }
      : checklist
    ));
  };

  const getSeriesEndBefore = (value: string) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return new Date(date.getTime() - 1).toISOString();
  };

  const updateExistingTaskEnd = async (taskId: string, recurrenceEndAt: string) => {
    const existingTask = tasks.find(item => item.id === taskId);
    if (!existingTask) return;
    const existingEndTime = getTime(existingTask.recurrenceEndAt);
    const nextEndTime = getTime(recurrenceEndAt);
    if (existingEndTime !== null && nextEndTime !== null && nextEndTime >= existingEndTime) return;
    await saveCalendarTaskOnline(calendarId, { ...existingTask, recurrenceEndAt });
  };

  const taskById = new Map(tasks.map(item => [item.id, item]));
  const getRootTaskId = (taskId?: string | null) => {
    if (!taskId) return taskId;
    const visited = new Set<string>();
    let currentId = taskId;
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const currentTask = taskById.get(currentId);
      if (!currentTask?.recurrenceParentId) return currentId;
      currentId = currentTask.recurrenceParentId;
    }
    return taskId;
  };

  const effectiveTaskId = seriesTaskId || (task ? getCalendarTaskSeriesId(task) : undefined);
  const baseTask = effectiveTaskId ? tasks.find(item => item.id === effectiveTaskId) : task;
  const rootTaskId = getRootTaskId(effectiveTaskId);
  const isRecurringTask = Boolean(baseTask && baseTask.repeatType !== 'none');
  const effectiveOccurrenceStartAt = occurrenceStartAt || task?.startAt || baseTask?.startAt;
  const isFutureScopeSplit = Boolean(
    isRecurringTask &&
    effectiveOccurrenceStartAt &&
    baseTask &&
    toDateKey(effectiveOccurrenceStartAt) !== toDateKey(baseTask.startAt)
  );
  const nextSeriesEndAt = isFutureScopeSplit && effectiveOccurrenceStartAt
    ? getSeriesEndBefore(effectiveOccurrenceStartAt)
    : undefined;
  const canChangeFutureScope = !isRecurringTask || !isFutureScopeSplit || (
    getTime(baseTask?.recurrenceEndAt) === null ||
    (getTime(nextSeriesEndAt) !== null && getTime(nextSeriesEndAt)! < getTime(baseTask?.recurrenceEndAt)!)
  );

  const isSameRecurrenceGroup = (item: CalendarTask) => getRootTaskId(item.id) === rootTaskId;

  const deleteFutureSplitSegments = async (fromStartAt: string, keepTaskId?: string) => {
    const boundaryTime = getTime(fromStartAt);
    if (boundaryTime === null || !rootTaskId) return;
    const futureSegments = tasks.filter(item => {
      if (item.id === keepTaskId) return false;
      if (!isSameRecurrenceGroup(item)) return false;
      const itemStartTime = getTime(item.startAt);
      return itemStartTime !== null && itemStartTime >= boundaryTime;
    });
    await Promise.all(futureSegments.map(item => deleteCalendarTaskOnline(calendarId, item.id)));
  };

  const saveTask = async (scope: 'single' | 'future' = 'future') => {
    if (isSaving) return;
    if (!title.trim()) {
      setError(t('calendar.task.errorTitle'));
      return;
    }
    if (durationTotal <= 0) {
      setError(t('calendar.task.errorDuration'));
      return;
    }

    let usedAutoStart = false;
    let nextStartAt = fromLocalInputValue(startAt);
    if (!nextStartAt) {
      nextStartAt = findNearestFreeStart(tasks.filter(item => item.id !== task?.id), durationTotal);
      usedAutoStart = true;
    }
    setIsSaving(true);

    const occurrenceDateKey = occurrenceDate || (occurrenceStartAt ? toDateKey(occurrenceStartAt) : toDateKey(nextStartAt));
    const nextTaskData = {
      id: effectiveTaskId,
      title: title.trim(),
      description: description.trim(),
      durationMinutes: durationTotal,
      startAt: nextStartAt,
      deadlineAt: fromLocalInputValue(deadlineAt) || undefined,
      repeatType,
      customRepeatDays,
      priority: priority || undefined,
      labelIds: selectedLabelIds,
      headerColor,
      checklists,
      attachments,
      isCompleted: task?.isCompleted || false,
      sourceBoardCardId: initial.sourceBoardCardId,
      recurrenceExceptionDates: baseTask?.recurrenceExceptionDates || [],
      recurrenceParentId: baseTask?.recurrenceParentId,
      recurrenceEndAt: baseTask?.recurrenceEndAt,
    };

    try {
      let savedTask: CalendarTask;
      if (isRecurringTask && scope === 'single') {
        const savedOccurrence = await saveCalendarTaskOccurrenceOnline(calendarId, effectiveTaskId!, occurrenceDateKey, {
          title: nextTaskData.title,
          description: nextTaskData.description,
          durationMinutes: nextTaskData.durationMinutes,
          startAt: nextStartAt,
          deadlineAt: nextTaskData.deadlineAt,
          priority: nextTaskData.priority,
          labelIds: nextTaskData.labelIds,
          headerColor: nextTaskData.headerColor,
          checklists: nextTaskData.checklists,
          attachments: nextTaskData.attachments,
          isCompleted: nextTaskData.isCompleted,
        });
        savedTask = (savedOccurrence || { ...(baseTask || task!), ...nextTaskData, id: effectiveTaskId! }) as CalendarTask;
      } else if (isRecurringTask && !canChangeFutureScope) {
        setError(t('calendar.task.noFutureOccurrences'));
        return;
      } else if (isRecurringTask && occurrenceStartAt && toDateKey(occurrenceStartAt) !== toDateKey(baseTask!.startAt)) {
        await deleteFutureSplitSegments(occurrenceStartAt, effectiveTaskId);
        await updateExistingTaskEnd(effectiveTaskId!, getSeriesEndBefore(occurrenceStartAt));
        savedTask = await saveCalendarTaskOnline(calendarId, {
          ...nextTaskData,
          id: undefined,
          recurrenceParentId: rootTaskId || effectiveTaskId,
          recurrenceExceptionDates: [],
          recurrenceEndAt: undefined,
        });
      } else {
        if (isRecurringTask && occurrenceStartAt) {
          await deleteFutureSplitSegments(occurrenceStartAt, effectiveTaskId);
        }
        savedTask = await saveCalendarTaskOnline(calendarId, nextTaskData);
      }
      await onSaved(savedTask, usedAutoStart);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTask = async (scope: 'single' | 'future' = 'future') => {
    if (!task) return;
    if (isRecurringTask && scope === 'single') {
      const occurrenceDateKey = occurrenceDate || (occurrenceStartAt ? toDateKey(occurrenceStartAt) : toDateKey(task.startAt));
      await deleteCalendarTaskOccurrenceOnline(calendarId, effectiveTaskId!, occurrenceDateKey);
    } else if (isRecurringTask && !canChangeFutureScope) {
      setError(t('calendar.task.noFutureOccurrences'));
      return;
    } else if (isRecurringTask && occurrenceStartAt && toDateKey(occurrenceStartAt) !== toDateKey(baseTask!.startAt)) {
      await deleteFutureSplitSegments(occurrenceStartAt, effectiveTaskId);
      await updateExistingTaskEnd(effectiveTaskId!, getSeriesEndBefore(occurrenceStartAt));
    } else {
      if (isRecurringTask && occurrenceStartAt) {
        await deleteFutureSplitSegments(occurrenceStartAt);
      } else {
        await deleteCalendarTaskOnline(calendarId, effectiveTaskId!);
      }
    }
    await onDeleted?.();
  };

  return (
    <div className="modal-overlay calendar-task-modal-overlay" onClick={onClose}>
      <div className="modal-content calendar-task-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{task ? t('calendar.task.edit') : t('calendar.task.create')}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('common.close')}>
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className="calendar-task-form">
          {error && <div className="calendar-task-error">{error}</div>}

          <label className="calendar-task-field full">
            <span>{t('calendar.task.title')} *</span>
            <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} />
          </label>

          <label className="calendar-task-field full">
            <span>{t('calendar.task.description')}</span>
            <textarea className="form-input" value={description} onChange={e => setDescription(e.target.value)} rows={3} />
          </label>

          <div className="calendar-task-field">
            <span>{t('calendar.task.duration')} *</span>
            <div className="calendar-task-inline">
              <input className="form-input" type="number" min="0" value={durationHours} onChange={e => setDurationHours(e.target.value)} />
              <input className="form-input" type="number" min="0" max="59" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} />
            </div>
          </div>

          <label className="calendar-task-field">
            <span>{t('calendar.task.startAt')}</span>
            <input className="form-input" type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} />
          </label>

          <label className="calendar-task-field">
            <span>{t('calendar.task.deadline')}</span>
            <input className="form-input" type="datetime-local" value={deadlineAt} onChange={e => setDeadlineAt(e.target.value)} />
          </label>

          <div className="calendar-task-field">
            <span>{t('calendar.task.repeat')}</span>
            <select className="form-input" value={repeatType} onChange={e => setRepeatType(e.target.value as CalendarRepeatType)}>
              <option value="none">{t('calendar.repeat.none')}</option>
              <option value="daily">{t('calendar.repeat.daily')}</option>
              <option value="weekly">{t('calendar.repeat.weekly')}</option>
              <option value="monthly">{t('calendar.repeat.monthly')}</option>
              <option value="custom">{t('calendar.repeat.custom')}</option>
            </select>
          </div>

          {repeatType === 'custom' && (
            <div className="calendar-task-field full">
              <span>{t('calendar.task.customRepeat')}</span>
              <div className="calendar-task-days">
                {weekDays.map(day => (
                  <label key={day} className={customRepeatDays.includes(day) ? 'active' : ''}>
                    <input
                      type="checkbox"
                      checked={customRepeatDays.includes(day)}
                      onChange={e => setCustomRepeatDays(prev => e.target.checked ? [...prev, day] : prev.filter(item => item !== day))}
                    />
                    {t(`calendar.weekday.${day}`)}
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="calendar-task-field">
            <span>{t('calendar.task.priority')}</span>
            <select className="form-input" value={priority} onChange={e => setPriority(e.target.value as CalendarPriority | '')}>
              <option value="">{t('calendar.priority.none')}</option>
              <option value="high">{t('calendar.priority.high')}</option>
              <option value="medium">{t('calendar.priority.medium')}</option>
              <option value="low">{t('calendar.priority.low')}</option>
            </select>
          </label>

          <div className="calendar-task-field">
            <span>{t('calendar.task.headerColor')}</span>
            <div className="calendar-task-swatches">
              {colorOptions.map(color => (
                <button key={color} type="button" className={headerColor === color ? 'active' : ''} style={{ background: color }} onClick={() => setHeaderColor(color)} />
              ))}
            </div>
          </div>

          <div className="calendar-task-field full">
            <span>{t('calendar.task.labels')}</span>
            <div className="calendar-task-label-list">
              {labels.map(label => (
                <button key={label.id} type="button" className={selectedLabelIds.includes(label.id) ? 'active' : ''} onClick={() => toggleLabel(label.id)}>
                  <i style={{ background: label.color }} />
                  {label.name}
                </button>
              ))}
            </div>
            <div className="calendar-task-inline">
              <input className="form-input" value={newLabelName} onChange={e => setNewLabelName(e.target.value)} placeholder={t('calendar.task.newLabelName')} />
              <input className="form-input" type="color" value={newLabelColor} onChange={e => setNewLabelColor(e.target.value)} />
              <button type="button" className="calendar-task-inline-button" onClick={handleCreateLabel}>{t('common.add')}</button>
            </div>
          </div>

          <div className="calendar-task-field full">
            <span>{t('calendar.task.checklists')}</span>
            {checklists.map(checklist => (
              <div key={checklist.id} className="calendar-task-checklist">
                <div className="calendar-task-checklist-header">
                  <strong>{checklist.title}</strong>
                  <button type="button" onClick={() => setChecklists(prev => prev.filter(item => item.id !== checklist.id))}>
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </div>
                {checklist.items.map(item => (
                  <label key={item.id} className="calendar-task-checklist-item">
                    <input
                      type="checkbox"
                      checked={item.isChecked}
                      onChange={e => setChecklists(prev => prev.map(list => list.id === checklist.id
                        ? { ...list, items: list.items.map(existing => existing.id === item.id ? { ...existing, isChecked: e.target.checked } : existing) }
                        : list
                      ))}
                    />
                    <input
                      className="form-input"
                      value={item.text}
                      onChange={e => setChecklists(prev => prev.map(list => list.id === checklist.id
                        ? { ...list, items: list.items.map(existing => existing.id === item.id ? { ...existing, text: e.target.value } : existing) }
                        : list
                      ))}
                    />
                    <button
                      type="button"
                      onClick={() => setChecklists(prev => prev.map(list => list.id === checklist.id
                        ? { ...list, items: list.items.filter(existing => existing.id !== item.id) }
                        : list
                      ))}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </label>
                ))}
                <input
                  className="form-input"
                  placeholder={t('checklist.itemPlaceholder')}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      addChecklistItem(checklist.id, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
              </div>
            ))}
            <div className="calendar-task-inline">
              <input className="form-input" value={newChecklistTitle} onChange={e => setNewChecklistTitle(e.target.value)} placeholder={t('checklist.title')} />
              <button type="button" className="calendar-task-inline-button" onClick={addChecklist}>{t('common.add')}</button>
            </div>
          </div>

          <div className="calendar-task-field full">
            <span>{t('calendar.task.attachments')}</span>
            <label className="calendar-task-attachment-button">
              <FiPaperclip aria-hidden="true" />
              {t('calendar.task.addAttachment')}
              <input
                type="file"
                multiple
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  setAttachments(prev => [...prev, ...files.map(file => ({ id: makeId(), name: file.name, url: URL.createObjectURL(file) }))]);
                }}
              />
            </label>
            <div className="calendar-task-attachments">
              {attachments.map(attachment => (
                <span key={attachment.id}>
                  <a
                    href={attachment.url || attachment.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => {
                      if (!attachment.url && !attachment.name.startsWith('http') && !attachment.name.startsWith('/')) {
                        event.preventDefault();
                      }
                    }}
                  >
                    {getAttachmentDisplayName(attachment)}
                  </a>
                  <button type="button" onClick={() => setAttachments(prev => prev.filter(item => item.id !== attachment.id))}>
                    <FiTrash2 aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {task && task.repeatType !== 'none' && (
            <>
              <button type="button" className="calendar-task-footer-button danger" disabled={isSaving} onClick={() => handleDeleteTask('single')}>
                {t('calendar.task.deleteCurrent')}
              </button>
              <button type="button" className="calendar-task-footer-button danger" disabled={isSaving || !canChangeFutureScope} onClick={() => handleDeleteTask('future')}>
                {t('calendar.task.deleteFuture')}
              </button>
            </>
          )}
          {task && task.repeatType === 'none' && (
            <button type="button" className="calendar-task-footer-button danger" disabled={isSaving} onClick={() => handleDeleteTask('future')}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="calendar-task-footer-button secondary" onClick={onClose}>{t('common.cancel')}</button>
          {task && task.repeatType !== 'none' ? (
            <>
              <button type="button" className="calendar-task-footer-button primary" disabled={isSaving} onClick={() => saveTask('single')}>
                {t('calendar.task.saveCurrent')}
              </button>
              <button type="button" className="calendar-task-footer-button primary" disabled={isSaving || !canChangeFutureScope} onClick={() => saveTask('future')}>
                {t('calendar.task.saveFuture')}
              </button>
            </>
          ) : (
            <button type="button" className="calendar-task-footer-button primary" disabled={isSaving} onClick={() => saveTask('future')}>
              {task ? t('common.save') : t('calendar.task.add')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
